/**
 * In-game HUD: resources, build panel, minimap, selection readout.
 *
 * Plain DOM rather than drawn into the canvas. Text stays crisp at any zoom,
 * it is accessible to screen readers and browser zoom, and it keeps UI layout
 * out of the render hot path.
 */

import {
  KIND_BARRACKS,
  KIND_HARVESTER,
  KIND_HQ,
  KIND_INFANTRY,
  KIND_REFINERY,
  KIND_TANK,
  KIND_WAR_FACTORY,
  QUEUE_INFANTRY,
  QUEUE_STRUCTURE,
  QUEUE_VEHICLE,
  TILE_ORE,
  TILE_ROCK,
  TILE_WATER,
  queueOf,
} from '../game/constants.js';
import { teamColor } from '../render/art.js';

export interface BuildItem {
  kind: number;
  label: string;
  hotkey: string;
}

export const BUILD_ITEMS: BuildItem[] = [
  { kind: KIND_INFANTRY, label: 'Rifle Infantry', hotkey: 'Q' },
  { kind: KIND_TANK, label: 'Battle Tank', hotkey: 'W' },
  { kind: KIND_HARVESTER, label: 'Ore Harvester', hotkey: 'E' },
  { kind: KIND_REFINERY, label: 'Ore Refinery', hotkey: 'R' },
  { kind: KIND_BARRACKS, label: 'Barracks', hotkey: 'T' },
  { kind: KIND_WAR_FACTORY, label: 'War Factory', hotkey: 'Y' },
];

export interface HudCallbacks {
  onProduce(kind: number): void;
  onCancel(kind: number): void;
  onMinimapJump(x: number, y: number): void;
}

interface ButtonRefs {
  root: HTMLButtonElement;
  progress: HTMLElement;
  badge: HTMLElement;
  cost: HTMLElement;
}

export class Hud {
  readonly root: HTMLElement;
  private creditsEl: HTMLElement;
  private statusEl: HTMLElement;
  private selectionEl: HTMLElement;
  private buttons = new Map<number, ButtonRefs>();
  private minimap: HTMLCanvasElement;
  private minimapCtx: CanvasRenderingContext2D;
  private banner: HTMLElement;
  private mapWidth = 128;
  private mapHeight = 128;
  private terrainCache: ImageData | null = null;

  constructor(
    parent: HTMLElement,
    private costOf: (kind: number) => number,
    private cb: HudCallbacks,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'hud';

    // Top bar: credits and connection status.
    const top = document.createElement('div');
    top.className = 'hud-top';
    this.creditsEl = document.createElement('div');
    this.creditsEl.className = 'credits';
    this.statusEl = document.createElement('div');
    this.statusEl.className = 'status';
    top.append(this.creditsEl, this.statusEl);

    // Side panel: minimap above the build list.
    const side = document.createElement('div');
    side.className = 'hud-side';

    this.minimap = document.createElement('canvas');
    this.minimap.className = 'minimap';
    this.minimap.width = 192;
    this.minimap.height = 192;
    this.minimapCtx = this.minimap.getContext('2d')!;
    this.attachMinimap();

    const build = document.createElement('div');
    build.className = 'build-panel';
    for (const item of BUILD_ITEMS) {
      build.appendChild(this.makeButton(item));
    }

    side.append(this.minimap, build);

    this.selectionEl = document.createElement('div');
    this.selectionEl.className = 'selection-info';

    this.banner = document.createElement('div');
    this.banner.className = 'banner hidden';

    this.root.append(top, side, this.selectionEl, this.banner);
    parent.appendChild(this.root);
  }

  private makeButton(item: BuildItem): HTMLElement {
    const btn = document.createElement('button');
    btn.className = 'build-btn';
    btn.type = 'button';

    const name = document.createElement('span');
    name.className = 'build-name';
    name.textContent = item.label;

    const key = document.createElement('span');
    key.className = 'build-key';
    key.textContent = item.hotkey;

    const cost = document.createElement('span');
    cost.className = 'build-cost';
    cost.textContent = `$${this.costOf(item.kind)}`;

    const progress = document.createElement('span');
    progress.className = 'build-progress';

    const badge = document.createElement('span');
    badge.className = 'build-badge hidden';

    btn.append(progress, name, key, cost, badge);

    btn.addEventListener('click', (e) => {
      if (e.shiftKey) this.cb.onCancel(item.kind);
      else this.cb.onProduce(item.kind);
    });
    btn.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.cb.onCancel(item.kind);
    });

    this.buttons.set(item.kind, { root: btn, progress, badge, cost });
    return btn;
  }

  private attachMinimap(): void {
    const jump = (e: PointerEvent) => {
      const rect = this.minimap.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * this.mapWidth;
      const y = ((e.clientY - rect.top) / rect.height) * this.mapHeight;
      this.cb.onMinimapJump(x, y);
    };
    this.minimap.addEventListener('pointerdown', (e) => {
      this.minimap.setPointerCapture(e.pointerId);
      jump(e);
    });
    this.minimap.addEventListener('pointermove', (e) => {
      if (e.buttons & 1) jump(e);
    });
  }

  setCredits(value: number): void {
    this.creditsEl.textContent = `$${value.toLocaleString()}`;
  }

  setStatus(text: string, tone: 'ok' | 'warn' | 'bad' = 'ok'): void {
    this.statusEl.textContent = text;
    this.statusEl.dataset.tone = tone;
  }

  setSelection(summary: string): void {
    this.selectionEl.textContent = summary;
    this.selectionEl.classList.toggle('hidden', summary === '');
  }

  showBanner(text: string, sub = ''): void {
    this.banner.innerHTML = '';
    const h = document.createElement('div');
    h.className = 'banner-title';
    h.textContent = text;
    this.banner.appendChild(h);
    if (sub) {
      const p = document.createElement('div');
      p.className = 'banner-sub';
      p.textContent = sub;
      this.banner.appendChild(p);
    }
    this.banner.classList.remove('hidden');
  }

  hideBanner(): void {
    this.banner.classList.add('hidden');
  }

  /**
   * Refreshes build buttons: affordability, queue depth and progress on the
   * item currently being produced.
   */
  updateBuildPanel(
    credits: number,
    queueItems: (queue: number) => Uint8Array,
    queueProgress: (queue: number) => number,
    pendingStructure: number,
  ): void {
    const queues = [QUEUE_STRUCTURE, QUEUE_INFANTRY, QUEUE_VEHICLE].map((q) => ({
      q,
      items: queueItems(q),
      progress: queueProgress(q),
    }));

    for (const [kind, refs] of this.buttons) {
      const q = queues[queueOf(kind) === QUEUE_STRUCTURE ? 0 : queueOf(kind) === QUEUE_INFANTRY ? 1 : 2];
      const count = [...q.items].filter((k) => k === kind).length;
      const isFront = q.items.length > 0 && q.items[0] === kind;
      const affordable = credits >= this.costOf(kind);

      refs.root.classList.toggle('affordable', affordable);
      refs.root.classList.toggle('busy', count > 0);
      refs.badge.classList.toggle('hidden', count === 0);
      refs.badge.textContent = count > 1 ? `x${count}` : '';
      refs.progress.style.width = isFront ? `${q.progress}%` : '0%';

      if (pendingStructure === kind) {
        refs.root.classList.add('ready');
        refs.cost.textContent = 'PLACE IT';
      } else {
        refs.root.classList.remove('ready');
        refs.cost.textContent = `$${this.costOf(kind)}`;
      }
    }
  }

  setMap(width: number, height: number, tiles: Uint8Array): void {
    this.mapWidth = width;
    this.mapHeight = height;
    this.renderTerrainCache(tiles);
  }

  /**
   * Rasterises terrain once into an offscreen ImageData. Redrawing the minimap
   * each frame then costs one putImageData plus the unit dots, instead of
   * re-reading 16k tiles.
   */
  private renderTerrainCache(tiles: Uint8Array): void {
    const w = this.mapWidth;
    const h = this.mapHeight;
    const img = this.minimapCtx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
      const t = tiles[i];
      let r = 62;
      let g = 82;
      let b = 48;
      if (t === TILE_WATER) {
        r = 29;
        g = 58;
        b = 92;
      } else if (t === TILE_ROCK) {
        r = 74;
        g = 74;
        b = 79;
      } else if (t === TILE_ORE) {
        r = 190;
        g = 155;
        b = 40;
      }
      img.data[i * 4] = r;
      img.data[i * 4 + 1] = g;
      img.data[i * 4 + 2] = b;
      img.data[i * 4 + 3] = 255;
    }
    this.terrainCache = img;
  }

  /** Redraws the minimap: terrain, unit dots, and the current view rectangle. */
  drawMinimap(
    entities: Float32Array,
    count: number,
    stride: number,
    localPlayer: number,
    view: { x: number; y: number; halfW: number; halfH: number },
  ): void {
    const ctx = this.minimapCtx;
    const cw = this.minimap.width;
    const ch = this.minimap.height;

    ctx.clearRect(0, 0, cw, ch);
    if (this.terrainCache) {
      // Scale the cached terrain up to the minimap size.
      ctx.imageSmoothingEnabled = false;
      const tmp = document.createElement('canvas');
      tmp.width = this.mapWidth;
      tmp.height = this.mapHeight;
      tmp.getContext('2d')!.putImageData(this.terrainCache, 0, 0);
      ctx.drawImage(tmp, 0, 0, cw, ch);
    }

    const sx = cw / this.mapWidth;
    const sy = ch / this.mapHeight;

    for (let i = 0; i < count; i++) {
      const o = i * stride;
      const owner = entities[o + 6] | 0;
      const kind = entities[o + 5] | 0;
      const col = teamColor(owner);
      ctx.fillStyle = `rgb(${(col.r * 255) | 0},${(col.g * 255) | 0},${(col.b * 255) | 0})`;
      const size = kind >= KIND_HQ ? 4 : 2.5;
      ctx.fillRect(entities[o] * sx - size / 2, entities[o + 1] * sy - size / 2, size, size);
    }

    // View rectangle.
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(
      (view.x - view.halfW) * sx,
      (view.y - view.halfH) * sy,
      view.halfW * 2 * sx,
      view.halfH * 2 * sy,
    );

    void localPlayer;
  }
}
